
import Vue from "vue";
import Papa from "papaparse";
import TempStarsTableTitle from "@/components/shared/TempStarsTableTitle/TempStarsTableTitle.vue";
import {
  getCurrentAdminRole,
  bulkUploadPlacementFees,
  BulkPlacementFeeResponse,
  BulkPlacementFeeRow,
  BulkPlacementFeeRowResult
} from "@/api";

interface ParsedRow {
  line: number;
  dentistId: string;
  email: string;
  numCredits: string;
  reason: string;
  validationError: string | null;
}

const MAX_ROWS = 1000;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const validateRow = (row: ParsedRow): string | null => {
  const did = parseInt(row.dentistId, 10);
  if (!did || Number.isNaN(did) || did < 1) {
    return "dentistId must be a positive integer";
  }
  if (!row.email || !EMAIL_REGEX.test(row.email)) {
    return "email is required and must be a valid email address";
  }
  const credits = parseInt(row.numCredits, 10);
  if (!credits || Number.isNaN(credits) || credits < 1 || credits > 100) {
    return "numCredits must be an integer between 1 and 100";
  }
  return null;
};

export default Vue.extend({
  name: "BulkPlacementFeeUpload",
  components: { TempStarsTableTitle },
  data() {
    return {
      accessChecked: false,
      isDeveloper: false,
      csvFile: null as File | null,
      fileError: "" as string,
      batchNote: "" as string,
      noteError: "" as string,
      parsedRows: [] as ParsedRow[],
      submitting: false,
      result: null as BulkPlacementFeeResponse | null,
      snackbar: false,
      snackbarText: "",
      snackbarColor: "success",
      previewHeaders: [
        { text: "#", value: "line", width: 60 },
        { text: "dentistId", value: "dentistId" },
        { text: "email", value: "email" },
        { text: "numCredits", value: "numCredits" },
        { text: "reason", value: "reason" },
        { text: "Validation", value: "validationError" }
      ],
      resultHeaders: [
        { text: "#", value: "row", width: 60 },
        { text: "Status", value: "status", width: 100 },
        { text: "dentistId", value: "dentistId" },
        { text: "email", value: "email" },
        { text: "numCredits", value: "numCredits" },
        { text: "New total", value: "newTotal" },
        { text: "Practice", value: "practiceName" },
        { text: "Error", value: "error" }
      ]
    };
  },
  computed: {
    clientValidCount(): number {
      return this.parsedRows.filter((r: ParsedRow) => !r.validationError).length;
    },
    clientInvalidCount(): number {
      return this.parsedRows.filter((r: ParsedRow) => !!r.validationError).length;
    },
    canSubmit(): boolean {
      return (
        this.isDeveloper &&
        !this.submitting &&
        !!this.batchNote.trim() &&
        this.clientValidCount > 0 &&
        !this.fileError
      );
    }
  },
  async created() {
    try {
      const me = await getCurrentAdminRole();
      this.isDeveloper = !!me.isDeveloper;
    } catch (error) {
      this.isDeveloper = false;
    } finally {
      this.accessChecked = true;
    }
  },
  methods: {
    onFileSelected(file: File | null) {
      this.fileError = "";
      this.parsedRows = [];
      this.result = null;
      if (!file) return;

      const name = (file.name || "").toLowerCase();
      const type = file.type || "";
      const isCsv =
        name.endsWith(".csv") && (type === "" || type === "text/csv" || type === "application/vnd.ms-excel");
      if (!isCsv) {
        this.fileError = "Only .csv files are accepted. Excel files must be saved as CSV.";
        this.csvFile = null;
        return;
      }

      Papa.parse<Record<string, string>>(file, {
        header: true,
        skipEmptyLines: true,
        transformHeader: (h: string) => h.trim(),
        complete: (results) => {
          if (results.errors && results.errors.length) {
            this.fileError = `CSV parse error: ${results.errors[0].message}`;
            return;
          }
          const data = results.data || [];
          if (data.length === 0) {
            this.fileError = "CSV has no data rows.";
            return;
          }
          if (data.length > MAX_ROWS) {
            this.fileError = `Too many rows (${data.length}). Maximum per batch is ${MAX_ROWS}.`;
            return;
          }

          const rows: ParsedRow[] = data.map(
            (row: Record<string, string>, idx: number) => {
              const parsed: ParsedRow = {
                line: idx + 1,
                dentistId: String(row["dentistId"] || "").trim(),
                email: String(row["email"] || "").trim().toLowerCase(),
                numCredits: String(row["numCredits"] || "").trim(),
                reason: String(row["reason"] || "").trim(),
                validationError: null
              };
              parsed.validationError = validateRow(parsed);
              return parsed;
            }
          );

          // Flag in-batch duplicates client-side too (server will re-check).
          const seen = new Map<string, number[]>();
          rows.forEach((r, idx) => {
            if (r.validationError) return;
            const key = `${r.dentistId}::${r.email}`;
            const list = seen.get(key) || [];
            list.push(idx);
            seen.set(key, list);
          });
          seen.forEach((indexes) => {
            if (indexes.length > 1) {
              indexes.forEach((i) => {
                rows[i].validationError =
                  "duplicate (dentistId, email) in this batch";
              });
            }
          });

          this.parsedRows = rows;
        },
        error: (err: Error) => {
          this.fileError = `Failed to read CSV: ${err.message}`;
        }
      });
    },

    resetForm() {
      this.csvFile = null;
      this.batchNote = "";
      this.noteError = "";
      this.fileError = "";
      this.parsedRows = [];
      this.result = null;
    },

    async submit() {
      this.noteError = "";
      if (!this.batchNote.trim()) {
        this.noteError = "A non-empty audit note is required";
        return;
      }
      if (this.clientValidCount === 0) {
        this.snackbarText = "No valid rows to upload";
        this.snackbarColor = "error";
        this.snackbar = true;
        return;
      }

      const rows: BulkPlacementFeeRow[] = this.parsedRows
        .filter((r: ParsedRow) => !r.validationError)
        .map((r: ParsedRow) => ({
          dentistId: parseInt(r.dentistId, 10),
          email: r.email,
          numCredits: parseInt(r.numCredits, 10),
          reason: r.reason || undefined
        }));

      this.submitting = true;
      try {
        const response = await bulkUploadPlacementFees({
          note: this.batchNote.trim(),
          rows
        });
        this.result = response;
        this.snackbarText = `Uploaded — ${response.successCount} succeeded, ${response.failureCount} failed`;
        this.snackbarColor = response.failureCount > 0 ? "warning" : "success";
        this.snackbar = true;
      } catch (error) {
        const err = error as Error;
        this.snackbarText = `Upload failed: ${err.message || "server error"}`;
        this.snackbarColor = "error";
        this.snackbar = true;
      } finally {
        this.submitting = false;
      }
    },

    downloadTemplate() {
      const csv =
        "dentistId,email,numCredits,reason\n" +
        "12345,office@example.com,2,Spring 2026 campaign\n";
      this.downloadCsv(csv, "placement-fee-upload-template.csv");
    },

    downloadErrorCsv() {
      if (!this.result) return;
      const errors = this.result.results.filter(
        (r: BulkPlacementFeeRowResult) => r.status === "error"
      );
      const lines = ["row,dentistId,email,numCredits,error"];
      errors.forEach((r) => {
        const input = (r.input || {}) as Record<string, unknown>;
        const cells = [
          r.row,
          (input.dentistId as string | number | undefined) ?? "",
          (input.email as string | undefined) ?? "",
          (input.numCredits as string | number | undefined) ?? "",
          `"${(r.error || "").replace(/"/g, '""')}"`
        ];
        lines.push(cells.join(","));
      });
      this.downloadCsv(
        lines.join("\n"),
        `placement-fee-upload-errors-batch-${this.result.batchId}.csv`
      );
    },

    downloadCsv(content: string, filename: string) {
      const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  }
});
